import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../context/AppContext";
import "bootstrap/dist/css/bootstrap.min.css";
import { Link } from "react-router-dom";
import Form from "react-bootstrap/Form";
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import ic_edit from "../assets/images/ic_edit.svg";
import Active from "../assets/images/Active.png";
import inActive from "../assets/images/inActive.png";
import Loader from "./Loader";
import {
  getWithAuthCallWithErrorResponse,
  postWithAuthCall,
  putWithAuthCall,
} from "../api/ApiServices";
import ApiConfig from "../api/ApiConfig";
import { ToastMsg } from "./ToastMsg";

const EMPTY_PLAN = {
  _id: "",
  durationValue: "",
  durationUnit: "W",
  priceGBP: "",
  label: "",
  savingsText: "",
  sortOrder: 0,
};

// Advertising plans sold to B2B customers. The duration code (e.g. "2W") is
// derived from value + unit on the server — it's what a campaign stores and what
// the campaign expiry calculation parses, so only Weeks/Months are offered.
const AdPlans = () => {
  const { sidebar } = useContext(AppContext);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [plan, setPlan] = useState(EMPTY_PLAN);
  const [errMsg, setErrMsg] = useState({});

  useEffect(() => {
    getPlans();
  }, []);

  const getPlans = () => {
    setLoading(true);
    getWithAuthCallWithErrorResponse(ApiConfig.AD_PLANS)
      .then((res) => {
        setLoading(false);
        if (res.error) {
          ToastMsg("error", res.json?.message || "Could not load ad plans");
          setPlans([]);
        } else {
          setPlans(res.json?.data || []);
        }
      })
      .catch(() => {
        setLoading(false);
        setPlans([]);
      });
  };

  const openAdd = () => {
    setPlan(EMPTY_PLAN);
    setErrMsg({});
    setShowForm(true);
  };

  const openEdit = (p) => {
    setPlan({
      _id: p._id,
      durationValue: p.durationValue,
      durationUnit: p.durationUnit,
      priceGBP: p.priceGBP,
      label: p.label || "",
      savingsText: p.savingsText || "",
      sortOrder: p.sortOrder || 0,
    });
    setErrMsg({});
    setShowForm(true);
  };

  const validate = () => {
    const errors = {};
    const value = Number(plan.durationValue);
    if (!Number.isInteger(value) || value < 1) {
      errors.durationValue = "Enter a whole number of 1 or more";
    }
    if (!["W", "M"].includes(plan.durationUnit)) {
      errors.durationUnit = "Choose Weeks or Months";
    }
    const price = Number(plan.priceGBP);
    if (!Number.isFinite(price) || price < 0) {
      errors.priceGBP = "Enter a valid price";
    }
    setErrMsg(errors);
    return Object.keys(errors).length === 0;
  };

  const savePlan = () => {
    if (!validate()) return;

    const body = JSON.stringify({
      durationValue: Number(plan.durationValue),
      durationUnit: plan.durationUnit,
      priceGBP: Number(plan.priceGBP),
      label: plan.label,
      savingsText: plan.savingsText,
      sortOrder: Number(plan.sortOrder) || 0,
    });

    const request = plan._id
      ? putWithAuthCall(`${ApiConfig.AD_PLANS}/${plan._id}`, body)
      : postWithAuthCall(ApiConfig.AD_PLANS, body);

    request
      .then((response) => {
        if (response?.error) {
          ToastMsg("error", response.message || "Could not save the plan");
        } else {
          ToastMsg("success", response?.message || "Plan saved successfully");
          setShowForm(false);
          setPlan(EMPTY_PLAN);
          getPlans();
        }
      })
      .catch(() => ToastMsg("error", "Could not save the plan"));
  };

  // Soft delete / restore. The plan is kept so campaigns already bought on it
  // still resolve their stored duration code.
  const setActive = (p, isActive) => {
    putWithAuthCall(
      `${ApiConfig.AD_PLANS}/${p._id}`,
      JSON.stringify({ isActive })
    )
      .then((response) => {
        if (response?.error) {
          ToastMsg("error", response.message || "Could not update the plan");
        } else {
          ToastMsg(
            "success",
            isActive ? "Plan activated" : "Plan deactivated"
          );
          getPlans();
        }
      })
      .catch(() => ToastMsg("error", "Could not update the plan"));
  };

  const previewCode =
    plan.durationValue && plan.durationUnit
      ? `${plan.durationValue}${plan.durationUnit}`
      : "—";

  return (
    <main
      id="cx-main"
      className={sidebar ? "admin-main" : "cx-active admin-main"}
    >
      <div className="heading-btn-main">
        <div className="heading-common">
          <label htmlFor="">Ad Plans</label>
        </div>
        <div className="button-wrapper">
          <button className="artcle_btnFalse" type="button" onClick={openAdd}>
            Add Plan
          </button>
        </div>
      </div>

      {/* ======================= ADD / EDIT ========================== */}
      <Modal
        show={showForm}
        onHide={() => setShowForm(false)}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{plan._id ? "Edit Plan" : "Add Plan"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-4 mb-3">
              <Form.Label>Duration</Form.Label>
              <Form.Control
                type="number"
                min={1}
                placeholder="e.g. 2"
                value={plan.durationValue}
                onChange={(e) =>
                  setPlan({ ...plan, durationValue: e.target.value })
                }
              />
              {errMsg.durationValue && (
                <span className="text-danger">{errMsg.durationValue}</span>
              )}
            </div>

            <div className="col-md-4 mb-3">
              <Form.Label>Unit</Form.Label>
              <Form.Select
                value={plan.durationUnit}
                onChange={(e) =>
                  setPlan({ ...plan, durationUnit: e.target.value })
                }
              >
                <option value="W">Weeks</option>
                <option value="M">Months</option>
              </Form.Select>
              {errMsg.durationUnit && (
                <span className="text-danger">{errMsg.durationUnit}</span>
              )}
            </div>

            <div className="col-md-4 mb-3">
              <Form.Label>Price (£)</Form.Label>
              <Form.Control
                type="number"
                min={0}
                step="0.01"
                placeholder="e.g. 189.00"
                value={plan.priceGBP}
                onChange={(e) => setPlan({ ...plan, priceGBP: e.target.value })}
              />
              {errMsg.priceGBP && (
                <span className="text-danger">{errMsg.priceGBP}</span>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <Form.Label>Savings text (optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. [You save £9.00]"
                value={plan.savingsText}
                onChange={(e) =>
                  setPlan({ ...plan, savingsText: e.target.value })
                }
              />
            </div>

            <div className="col-md-3 mb-3">
              <Form.Label>Sort order</Form.Label>
              <Form.Control
                type="number"
                value={plan.sortOrder}
                onChange={(e) => setPlan({ ...plan, sortOrder: e.target.value })}
              />
            </div>

            <div className="col-md-3 mb-3">
              <Form.Label>Plan code</Form.Label>
              <Form.Control type="text" value={previewCode} readOnly disabled />
            </div>

            <div className="col-md-12 mb-3">
              <Form.Label>Custom label (optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Leave blank to build it from the duration and price"
                value={plan.label}
                onChange={(e) => setPlan({ ...plan, label: e.target.value })}
              />
              <small className="text-muted">
                This is what advertisers see in the duration dropdown.
              </small>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <div className="buttons-modal">
            <button className="me-2" type="button" onClick={savePlan}>
              {plan._id ? "Update Plan" : "Add Plan"}
            </button>
            <Link to="#" className="bg-danger" onClick={() => setShowForm(false)}>
              Cancel
            </Link>
          </div>
        </Modal.Footer>
      </Modal>
      {/* ======================= END ADD / EDIT ====================== */}

      <div className="table-wrapper">
        {loading ? (
          <div style={{ marginTop: "150px" }}>
            <Loader />
          </div>
        ) : (
          <div className="main-data-wrapper">
            <Table class="display" striped bordered hover>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Duration</th>
                  <th>Price (£)</th>
                  <th>Shown to advertisers</th>
                  <th>Sort</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {plans && plans.length > 0 ? (
                  plans.map((p, index) => (
                    <tr key={p._id || "plan" + index}>
                      <td>{p.code}</td>
                      <td>
                        {p.durationValue}{" "}
                        {p.durationUnit === "W" ? "Week" : "Month"}
                        {p.durationValue > 1 ? "s" : ""}
                      </td>
                      <td>{Number(p.priceGBP).toFixed(2)}</td>
                      <td>{p.displayLabel}</td>
                      <td>{p.sortOrder}</td>
                      <td>
                        <span
                          className={
                            p.isActive ? "active-status" : "inactive-status"
                          }
                        >
                          {p.isActive ? "Active" : "InActive"}
                        </span>
                      </td>
                      <td className="action">
                        <span>
                          <Link
                            to="#"
                            className="icon edit"
                            onClick={() => openEdit(p)}
                          >
                            <img src={ic_edit} alt="edit" />
                          </Link>
                        </span>
                        {p.isActive ? (
                          <Link
                            to="#"
                            className="icon delete"
                            title="Deactivate"
                            onClick={() => setActive(p, false)}
                          >
                            <img src={Active} alt="deactivate" />
                          </Link>
                        ) : (
                          <Link
                            to="#"
                            className="icon delete"
                            title="Activate"
                            onClick={() => setActive(p, true)}
                          >
                            <img src={inActive} alt="activate" />
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7}>
                      <div className="justify-content-center align-items-center text-danger text-center mt-3">
                        Data Not Found
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </main>
  );
};

export default AdPlans;
